import { Link, useLocation } from 'react-router'
import styled from 'styled-components'
import { breakPointsCss, colors, measurments } from '../utils/config'
import Logo from './Logo'
import { useState } from 'react'
import { useMemo } from 'react'
import { FeatureKey, LucideIconName } from '@bm-js/atlascloud-shared'
import { ROUTE } from '../cnst/router.cnst'
import { useActions, useWebsite } from '../utils/hooks'
import { TrKey } from '../cnst/translations/trKey.cnst'
import { useTr } from '../utils/useTr'
import { Icon } from '@bm-js/atlascloud-ui'

interface UserNavLink {
  to: string
  trKey: TrKey
  icon: LucideIconName
  insertIndex?: number
}

const StyledNavbar = styled.nav`
  position: fixed;
  background: ${colors.gray300};
  left: 0;
  bottom: 0;
  top: 0;
  height: 100%;
  width: ${measurments.internalNavWidth}px;
  z-index: 20;
  .nav-pad {
    padding: 1.5rem;
  }
  .links {
    display: flex;
    flex-direction: column;
  }
  .logout {
    margin-top: 1rem;
  }
  a.link-item {
    padding: 0.5rem;
    padding-left: 1.5rem;
    margin-bottom: 0.2rem;
    display: flex;
    align-items: center;
    span {
      margin-left: 0.5rem;
    }
  }
  .hamburger {
    display: none;
  }
  ${breakPointsCss.tablet} {
    .hamburger {
      display: flex;
      align-items: center;
      cursor: pointer;
      p {
        margin: 0;
        margin-right: 0.5rem;
        font-family: 'Inter', sans-serif;
        color: ${colors.primary400};
      }
    }
    display: flex;
    align-items: center;
    justify-content: space-between;
    top: 0;
    left: 0;
    bottom: revert;
    right: 0;
    height: ${measurments.navHeight}px;
    width: 100%;
    .nav-pad {
      padding-left: 1rem;
      padding-right: 1rem;
      padding-bottom: 0;
      padding-top: 0;
    }
    .items {
      .logout {
        margin-top: 0;
      }
      opacity: 0;
      pointer-events: none;
      padding-top: 1rem;
      padding-bottom: 1rem;
      position: absolute;
      top: -200px;
      left: 0;
      right: 0;
      background: ${colors.gray300};
      transition: 200ms ease-in-out;
    }
  }
  &.is-open {
    .items {
      opacity: 1;
      top: ${measurments.navHeight}px;
      pointer-events: initial;
    }
  }
`

const Navbar = () => {
  const { anyWebsite } = useWebsite()
  const { signOut } = useActions()
  const { tr } = useTr()

  const featureLinks = useMemo(() => {
    const links: Record<string, UserNavLink> = {
      [FeatureKey.customerUserLogins]: {
        to: ROUTE.CUSTOMER_USERS,
        trKey: TrKey.navbarItemUsers,
        icon: LucideIconName.Users,
        insertIndex: 4,
      },
      [FeatureKey.courses]: {
        to: ROUTE.COURSES,
        trKey: TrKey.navbarItemCourses,
        icon: LucideIconName.Lightbulb,
        insertIndex: 2,
      },
      [FeatureKey.blog]: {
        to: ROUTE.BLOG,
        trKey: TrKey.navbarItemBlog,
        icon: LucideIconName.Megaphone,
        insertIndex: 1,
      },
      [FeatureKey.calendar]: {
        to: ROUTE.CALENDAR,
        trKey: TrKey.navbarItemCalendar,
        icon: LucideIconName.Calendar,
        insertIndex: 1,
      },
      [FeatureKey.sponsors]: {
        to: ROUTE.SPONSORS,
        trKey: TrKey.navbarItemSponsors,
        icon: LucideIconName.Gift,
        insertIndex: 1,
      },
      [FeatureKey.eCommerce]: {
        to: ROUTE.ECOMMERCE,
        trKey: TrKey.navbarItemECommerce,
        icon: LucideIconName.ShoppingCart,
        insertIndex: 2,
      },
    }
    return links
  }, [])

  const links = useMemo(() => {
    const baseLinks: UserNavLink[] = [
      {
        to: ROUTE.DASHBOARD,
        trKey: TrKey.navbarItemDashboard,
        icon: LucideIconName.Keyboard,
      },

      {
        to: ROUTE.MEDIA,
        trKey: TrKey.navbarItemMedia,
        icon: LucideIconName.Files,
      },
      {
        to: ROUTE.USERS,
        trKey: TrKey.navbarItemAdministrators,
        icon: LucideIconName.User,
      },
      {
        to: ROUTE.ACCOUNT,
        trKey: TrKey.navbarItemAccount,
        icon: LucideIconName.UserRoundCog,
      },
    ]

    if (anyWebsite?.features?.length) {
      anyWebsite.features.forEach((feat) => {
        const feature = featureLinks[feat]
        if (feature) {
          baseLinks.splice(feature.insertIndex ?? 0, 0, feature as UserNavLink)
        }
      })
    }

    return baseLinks
  }, [anyWebsite, featureLinks])

  const [isOpen, setIsOpen] = useState(false)

  return (
    <StyledNavbar className={`${isOpen ? 'is-open' : ''}`}>
      <div className='nav-pad'>
        <Logo href={'/'} />
      </div>
      <div className='hamburger nav-pad' onClick={() => setIsOpen(!isOpen)}>
        <p className='menu'>{tr(TrKey.navbarMenu)}</p>
        <Icon
          name={isOpen ? LucideIconName.X : LucideIconName.Menu}
          className='text-primary-400 size-6'
        />
      </div>
      <div className='items'>
        <div className='links'>
          {links.map((link) => (
            <LinkItem
              link={link}
              key={link.to}
              closeDropDown={() => setIsOpen(false)}
            />
          ))}
        </div>
        <div className='logout'>
          <LinkItem
            closeDropDown={() => setIsOpen(false)}
            link={{
              to: null,
              icon: LucideIconName.SquareArrowLeft,
              trKey: TrKey.navbarItemLogout,
              action: signOut,
            }}
          />
        </div>
      </div>
    </StyledNavbar>
  )
}

export default Navbar

const StyledLinkItem = styled.div<{ $isActiveRoute: boolean }>`
  position: relative;
  &:after {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    content: '';
    height: 100%;
    width: 5px;
    background: ${colors.primary400};
    transition: 100ms ease-in-out;
    opacity: 0;
    ${({ $isActiveRoute }) =>
      $isActiveRoute ? 'opacity:' + 1 + '!important;' : ''}
    transition: 100ms ease-in-out;
  }
  svg {
    ${({ $isActiveRoute }) =>
      $isActiveRoute ? 'stroke:' + colors.primary400 + '!important;' : ''}
    transition: 100ms ease-in-out;
  }
  span {
    color: ${colors.blackAlpla300};
    ${({ $isActiveRoute }) =>
      $isActiveRoute ? 'color:' + colors.primary400 + '!important;' : ''}
    transition: 100ms ease-in-out;
  }
  &:hover {
    &:after {
      opacity: 1;
    }
    svg {
      stroke: ${colors.primary400};
    }
    span {
      color: ${colors.primary400};
    }
  }
  ${breakPointsCss.tablet} {
    &::after {
      opacity: 0 !important;
    }
  }
`

type LinkItemProps = {
  link: {
    to: string | null
    trKey: TrKey
    icon: LucideIconName
    action?: () => void
  }
  closeDropDown?: () => void
}

const LinkItem = ({ link, closeDropDown }: LinkItemProps) => {
  const location = useLocation()
  const { tr } = useTr()
  const pathnameStart = location.pathname?.split('/')[1]
  const isActiveRoute = `/${pathnameStart}` === link.to

  return (
    <StyledLinkItem
      $isActiveRoute={isActiveRoute}
      onClick={() => {
        closeDropDown?.()
        link.action?.()
      }}
    >
      <Link
        to={link.to ? `${link.to}` : '#'}
        reloadDocument={false}
        key={link.to}
        className='link-item clean'
      >
        <Icon name={link.icon} className='size-7 stroke-black/40' />
        <span>{tr(link.trKey)}</span>
      </Link>
    </StyledLinkItem>
  )
}
